import React, { useRef, useLayoutEffect, useCallback, useState } from 'react';
import { sanitizeProductDescriptionHtml } from '../../utils/sanitizeProductHtml';
import { stripHtmlToPlain } from '../../utils/productDescriptionHtml';
import './ProductDescriptionHtmlEditor.css';

export const DESCRIPTION_FORMAT = {
  PLAIN: 'plain',
  HTML: 'html',
};

const MAX_HTML_CHARS = 12000;

/** Clipboard HTML from Word / Google Docs / browsers — extract body fragment then sanitize. */
function sanitizeClipboardHtml(htmlString) {
  if (typeof htmlString !== 'string' || !htmlString.trim()) return '';
  try {
    const doc = new DOMParser().parseFromString(htmlString, 'text/html');
    const inner = doc.body?.innerHTML?.trim() || '';
    if (!inner) return '';
    return sanitizeProductDescriptionHtml(inner);
  } catch {
    return sanitizeProductDescriptionHtml(htmlString);
  }
}

const ProductDescriptionHtmlEditor = ({
  format,
  onFormatChange,
  value,
  onChange,
  plainMaxLength = 2000,
  editorKey = 'desc',
}) => {
  const editorRef = useRef(null);
  const lastEmittedRef = useRef('');
  const prevSyncRef = useRef({ editorKey: null, format: null });
  /** Tracks last format so we detect Plain → HTML and force-load `value` into the editor. */
  const prevFormatRef = useRef(format);

  const emitHtml = useCallback(() => {
    const el = editorRef.current;
    if (!el) return;
    let raw = el.innerHTML || '';
    if (raw.length > MAX_HTML_CHARS) {
      raw = raw.slice(0, MAX_HTML_CHARS);
      el.innerHTML = raw;
    }
    const clean = sanitizeProductDescriptionHtml(raw);
    lastEmittedRef.current = clean;
    onChange(clean);
  }, [onChange]);

  useLayoutEffect(() => {
    const el = editorRef.current;
    if (!el || format !== DESCRIPTION_FORMAT.HTML) {
      prevFormatRef.current = format;
      return;
    }
    const enteredHtmlFromPlain =
      prevFormatRef.current !== DESCRIPTION_FORMAT.HTML && format === DESCRIPTION_FORMAT.HTML;
    prevFormatRef.current = format;

    const force =
      enteredHtmlFromPlain ||
      prevSyncRef.current.editorKey !== editorKey ||
      prevSyncRef.current.format !== format;
    prevSyncRef.current = { editorKey, format };
    if (!force) return;
    const incoming = sanitizeProductDescriptionHtml(value || '');
    el.innerHTML = incoming;
    lastEmittedRef.current = incoming;
  }, [value, editorKey, format]);

  const run = useCallback(
    (command, commandValue = null) => {
      const el = editorRef.current;
      if (!el) return;
      el.focus();
      try {
        document.execCommand(command, false, commandValue);
      } catch {
        /* ignore */
      }
      emitHtml();
    },
    [emitHtml]
  );

  const onPaste = useCallback(
    (e) => {
      e.preventDefault();
      const html = e.clipboardData.getData('text/html');
      const plain = e.clipboardData.getData('text/plain') || '';

      let sanitized = '';
      if (html && html.trim()) {
        sanitized = sanitizeClipboardHtml(html);
      }

      if (sanitized && sanitized.trim()) {
        try {
          document.execCommand('insertHTML', false, sanitized);
        } catch {
          try {
            document.execCommand('insertText', false, plain || stripHtmlToPlain(sanitized));
          } catch {
            /* ignore */
          }
        }
      } else {
        try {
          document.execCommand('insertText', false, plain);
        } catch {
          /* ignore */
        }
      }
      emitHtml();
    },
    [emitHtml]
  );

  const [color, setColor] = useState('#333333');

  const plainTextLen = (value || '').replace(/<[^>]+>/g, '').length;

  /**
   * When leaving HTML mode, React state may not yet include the latest contenteditable
   * HTML (blur/onChange ordering). Read innerHTML here and pass htmlSnapshot so the
   * parent converts from the live editor, not stale state.
   */
  const handleFormatClick = (next) => {
    if (format === DESCRIPTION_FORMAT.HTML && next === DESCRIPTION_FORMAT.PLAIN) {
      const el = editorRef.current;
      if (el) {
        let raw = el.innerHTML || '';
        if (raw.length > MAX_HTML_CHARS) {
          raw = raw.slice(0, MAX_HTML_CHARS);
        }
        const clean = sanitizeProductDescriptionHtml(raw);
        let plainText = stripHtmlToPlain(clean);
        if (!plainText || !String(plainText).trim()) {
          plainText = stripHtmlToPlain(raw);
        }
        lastEmittedRef.current = clean;
        onFormatChange(next, { htmlSnapshot: clean, plainText });
        return;
      }
    }
    onFormatChange(next);
  };

  /** Keep focus in the editor/textarea until click runs so innerHTML / value stay reliable. */
  const preventFocusSteal = (e) => {
    e.preventDefault();
  };

  return (
    <div className="pd-html-editor">
      <div className="pd-html-editor__format-row">
        <span className="pd-html-editor__format-label">Description type</span>
        <div className="pd-html-editor__format-toggle" role="group">
          <button
            type="button"
            className={format === DESCRIPTION_FORMAT.HTML ? 'active' : ''}
            onMouseDown={preventFocusSteal}
            onClick={() => handleFormatClick(DESCRIPTION_FORMAT.HTML)}
          >
            Formatted (HTML)
            <small>Bold, color, bullet list</small>
          </button>
          <button
            type="button"
            className={format === DESCRIPTION_FORMAT.PLAIN ? 'active' : ''}
            onMouseDown={preventFocusSteal}
            onClick={() => handleFormatClick(DESCRIPTION_FORMAT.PLAIN)}
          >
            Plain text only
          </button>
        </div>
      </div>

      {format === DESCRIPTION_FORMAT.PLAIN && (
        <>
          <textarea
            className="pd-html-editor__textarea"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder="Write product description (plain text)"
            rows={6}
            maxLength={plainMaxLength}
          />
          <div className="pd-html-editor__count">
            {(value || '').length}/{plainMaxLength} characters
          </div>
        </>
      )}

      {format === DESCRIPTION_FORMAT.HTML && (
        <>
          <p className="pd-html-editor__help">
            <strong>How to use:</strong> type text, <strong>select</strong> words, then <strong>Bold</strong> or pick a <strong>color</strong> and
            &quot;Apply to selection&quot;. Use <strong>• List</strong> / <strong>1. List</strong> for bullet or numbered points.
          </p>
          <div className="pd-html-editor__toolbar" role="toolbar" aria-label="Text formatting">
            <button type="button" onClick={() => run('bold')} title="Bold">
              <strong>B</strong>
            </button>
            <button type="button" onClick={() => run('italic')} title="Italic">
              <em>I</em>
            </button>
            <button type="button" onClick={() => run('underline')} title="Underline">
              <u>U</u>
            </button>
            <span className="pd-html-editor__toolbar-sep" aria-hidden="true" />
            <label className="pd-html-editor__color-wrap">
              Color
              <input type="color" value={color} onChange={(e) => setColor(e.target.value)} title="Pick color" />
              <button type="button" className="pd-html-editor__apply-color" onClick={() => run('foreColor', color)}>
                Apply to selection
              </button>
            </label>
            <span className="pd-html-editor__toolbar-sep" aria-hidden="true" />
            <button type="button" onClick={() => run('insertUnorderedList')} title="Bullet list">
              • List
            </button>
            <button type="button" onClick={() => run('insertOrderedList')} title="Numbered list">
              1. List
            </button>
            <button type="button" onClick={() => run('formatBlock', 'p')} title="Paragraph">
              ¶
            </button>
            <button type="button" onClick={() => run('formatBlock', 'h4')} title="Heading">
              H
            </button>
            <span className="pd-html-editor__toolbar-sep" aria-hidden="true" />
            <button type="button" onClick={() => run('removeFormat')} title="Clear formatting">
              Clear
            </button>
          </div>
          <div
            key={editorKey}
            ref={editorRef}
            className="pd-html-editor__area"
            contentEditable
            suppressContentEditableWarning
            onInput={emitHtml}
            onBlur={emitHtml}
            onPaste={onPaste}
            data-placeholder="Type description. Select text for bold or color. Use list buttons for points."
          />
          <div className="pd-html-editor__count">
            ~{plainTextLen} visible characters · {(value || '').length}/{MAX_HTML_CHARS} HTML
          </div>
        </>
      )}
    </div>
  );
};

export default ProductDescriptionHtmlEditor;
